import DLabGrid from "../../components/DLabGrid/DLabGrid";
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BookingMetaByEquipment, DEFAULT_FILTER } from "../../constants";
import useChangeEndTimeModal from "./ChangeBookingEndTime/hooks/useChangeEndTimeModal";
import {
  buildProjectsLabel,
  filterParams,
  get4WeekStartAndEndInISOString,
  getBookedUserDisplay,
  getReservedForBookedUsers,
  replaceOffsetWithOther,
  valueOrEmpty,
} from "../../utils/helpers/text/index";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { getAllData, clearGetAllDataCache } from "../../utils/helpers/fetching";
import {
  GET_BOOKING_ROWS_BY_SITE_NONE,
  BOOKING_BY_SITE_AND_DATE,
} from "../../gql/bookingapi";
import { deriveShortDescription, MAX_RICH_DESCRIPTION_LENGTH, RichDescriptionCellRenderer } from "../../components/shared/RichDescription";
import { compose, withApollo } from "react-apollo";
import { connect, useSelector } from "react-redux";
import { loadPendingReservations as loadPendingReservationsAction } from "../booking-calendars/redux/actions";
import BookingMenuAction from "./BookingMenuAction";
import BookingsPaginationActions from "../../components/shared/BookingsPaginationActions";
import BackdropPortal from "../../components/shared/BackdropPortal";
import moment from "moment";
import { BookingContext } from "../booking/context";
import CheckInButton from "../../components/shared/CheckInButton/CheckInButton";
import { EN_DASH } from "@digitallab/grid-common-frontend-components";
import { BookingTypeChip } from "../../components/shared/BookingTypeChip";
import { BookingHazardousChip } from "../../components/shared/BookingHazardousChip";
import useInstrumentCache from "../../utils/hooks/useInstrumentCache";
import { useSharedFilterState } from "../bookings/SharedFilterContext";
import { onAgGridFilterApply, sanitizeFilterModelForGrid } from "../../components/EquipmentList/Service/filterService";
import { filterKeyMapping, TOOL_PANELS } from "../../components/EquipmentList/Constants/constants";
import { convertStrToObjFilters } from "../../components/EquipmentList/Service/equipmentService";
import { onAgGridColumnApply } from "../../components/EquipmentList/Service/columnService";
import SaveConfiguration from "../../components/EquipmentList/Components/SaveConfiguration";
import { useBooking } from "../../components/EquipmentList/Service/commonsService";
import { getBookingDisplayText } from "../../components/shared/helpFunctions";
import { hasCheckInState } from "../../components/shared/checkInVisibility";
import { hazardousStatusGetter } from "../../utils/helpers/data";
import {
  createDefaultCsvExportParams,
  createDefaultExcelExportParams,
  createClipboardCellProcessor,
  getExportableColumnKeys,
} from "../../utils/helpers/exportValueMapper";
import ProjectRenderer from "../equipment-list/ProjectRender";
import { DateWithRecurrenceCellRenderer } from "../../utils/helpers/text/DateWithRecurrenceCellRenderer";
import ChangeBookingEndTimeModal from "./ChangeBookingEndTime/ChangeBookingEndTimeModal";

const SEARCH_ONLY_FILTER_KEYS = new Set(["isHazardous", "shouldAssisted"]);

const sanitizeGridFilterModel = (filterModel = {}) => {
  return Object.fromEntries(Object.entries(filterModel).filter(([key]) => !SEARCH_ONLY_FILTER_KEYS.has(key)));
};

const EquipmentBookingList = ({
  client,
  resetAll,
  searchQuery,
  setSearchQuery,
  setSelectedDateRange,
  setDisableResetAll,
  selectedDateRange,
  setDropType,
  setIsDropBooking,
  setIsDialogOpen,
  selectedEquipment,
  setSelectedEquipment,
  setOpenMultiDeleteDialog,
  setRowEquipmentId,
  tempRowData,
  setTempRowData,
  loadPendingReservations,
  setPopupEnableStatus,
  showAdvanceFilter,
  savedFilter,
  displayPreset,
  defaultShowColumns = [],
  userFilters,
  userDisplayColumnMaster,
  setUserFilters,
  setUserDisplayColumnMaster,
  setDisplayPreset,
  setSavedFilter,
  myLabFilter,
  showSideBar,
  dataModel,
  localUserFilters,
  currentSideBarPanel,
  setCurrentSideBarPanel,
  setLastDisplayColumns,
  setLastFavFilter,
  shouldFetchData,
  innerTableFilters,
  setInnerTableFilters,
}) => {
  const { nextToken, data, fetching, dispatchAction } = useContext(PaginationContext);
  const { dispatchAction: bookingDispatchAction } = useContext(BookingContext);
  const user = useSelector((store) => store.user);
  const usersList = useSelector((state) => state.users.users);
  const [gridApi, setGridApi] = useState(null);
  const rowStyle = { background: "var(--one-color-cobas-accent-gray-100)" };

  const gridRef = useRef();
  const localUserFiltersFallbackRef = useRef(userFilters);
  const safeLocalUserFilters = localUserFilters || localUserFiltersFallbackRef;
  const abortRef = useRef(null);
  const debouncedFetchRef = useRef(null);
  const lastDateKeyRef = useRef(null);

  const hadDataRef = useRef(false);

  const { filterState, isFilterDataReady, loadFilterData, updateCurrentFilter } = useSharedFilterState();

  const {
    isChangeEndTimeOpen,
    bookingForEndTimeChange,
    handleOpenChangeEndTimeModal: changeEndTimeOpen,
    handleCloseChangeEndTimeModal,
    handleSaveEndTime,
  } = useChangeEndTimeModal({ client });

  const filterDefToNameMap = useMemo(() => {
    const map = new Map();
    (userFilters || []).forEach((f) => {
      const key = JSON.stringify(f?.filterDefinition || {});
      map.set(key, f?.filterName || f?.chipName || "Unnamed");
    });

    if (myLabFilter?.filterDefinition) {
      map.set(JSON.stringify(myLabFilter.filterDefinition), myLabFilter.filterName || "My Lab");
    }

    return map; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilters, myLabFilter, filterState?.currentFilter]);

  useEffect(() => {
    // BUG-FIX: Do NOT apply filterDefinition as an ag-grid column filter.
    // Data is pre-filtered server-side via OpenSearch (loadInstruments).
    // Applying filterDefinition to ag-grid caused empty lists because the
    // `location` field in row data is an object {building,floor,room}, but
    // filterDefinition stores it as a string → set filter never matches.
    // Clear grid filters once on predefined filter change.
    if (gridRef.current?.api) {
      gridRef.current.api.setFilterModel({});
    }
  }, [filterState?.currentFilter]);

  const onApplyFilterFromChip = useCallback(
    (savedFilterModel) => {
      if (savedFilterModel === undefined) return;
      const filterObj =
        savedFilterModel && typeof savedFilterModel === "string"
          ? JSON.parse(savedFilterModel)
          : savedFilterModel || DEFAULT_FILTER;

      const filterDefinitionObj = Object.keys(filterObj)?.includes("filterName")
        ? convertStrToObjFilters(savedFilterModel)?.filterDefinition
        : filterObj;

      onAgGridFilterApply({
        gridRef,
        savedFilterModel: filterDefinitionObj,
        filterKeyMapping,
      });

      const key = JSON.stringify(filterDefinitionObj);
      const matchedName = filterDefToNameMap.get(key) || "Custom";

      if (setSavedFilter && matchedName !== "Custom") {
        setSavedFilter(matchedName);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [setCurrentSideBarPanel],
  );

  const onApplyColumnsFromChip = useCallback(
    (chipObj) => {
      const savedShowcolumns = chipObj?.displayDefinition?.showColumns;
      onApplyColumns(savedShowcolumns);
      if (setDisplayPreset) {
        setDisplayPreset({
          definition: chipObj.displayDefinition,
          label: chipObj.chipName,
          name: chipObj.id,
          ...chipObj,
        });
      } // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDisplayPreset],
  );

  const savedFilterChips = useMemo(() => {
    const base = (
      myLabFilter?.filterDefinition?.inventoryIds?.length > 0
        ? [myLabFilter, ...(userFilters || [])]
        : [...(userFilters || [])]
    ).map((f) => ({
      ...f,
      chipName: f.chipName || f.filterName,
      filterName: f.filterName,
      filterDefinition: f.filterDefinition,
      userID: f.userID || user?.id,
    }));

    return base; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilters, myLabFilter, filterState?.currentFilter, user?.id]);

  const savedColumnChips = useMemo(() => {
    return (userDisplayColumnMaster || []).map((d) => ({
      ...d,
      chipName: d.chipName || d.displayName,
      displayName: d.displayName,
      displayDefinition:
        typeof d.displayDefinition === "string" ? JSON.parse(d.displayDefinition) : d.displayDefinition,
      userID: d.userID || user?.id,
    }));
  }, [userDisplayColumnMaster, user?.id]);

  const instrumentCache = useInstrumentCache({
    client,
    user,
    masterDataFilters: filterState.masterDataFilters,
  });

  const statusGetter = (params) => {
    const data = params?.data;
    if (!data?.active) {
      return "Cancelled";
    }
    // Check if booking has hazardous materials and is pending approval
    const normalizedStatus = data?.approval?.status?.toString().toUpperCase();
    if (data?.hazardousDeclaration?.hasHazardousMaterials && normalizedStatus === "PENDING") {
      return "Pending";
    }
    return "Active";
  };
  const reservedForGetter = (params) => {
    const users = getReservedForBookedUsers(params?.data, usersList);
    if (!users.length) {
      return "-";
    }
    const display = getBookedUserDisplay(users[0]);
    const result = display.length > 10 ? display.substring(0, 10) + "... " : display;
    const suffix = users.length - 1 > 0 ? ` +${users.length - 1}` : "";
    return result + suffix;
  };
  const reservedForFilterFormatterGetter = (params) => {
    const users = getReservedForBookedUsers(params?.data, usersList);
    if (!users.length) {
      return "-";
    }
    return users.map(getBookedUserDisplay);
  };
  const projectGetter = (params) => {
    const row = params?.data || {};
    return buildProjectsLabel({
      rowRedPandaProjects: row.redPandaProjects,
      userRedPandaProjects: user?.redPandaProjects,
      projectId: row.project,
    });
  };
  const descriptionGetter = (params) => {
    const shortValue = params?.data?.description;
    if (typeof shortValue === "string" && shortValue.trim().length > 0) {
      return shortValue;
    }
    const richValue = params?.data?.richDescription;
    if (typeof richValue === "string" && richValue.trim().length > 0) {
      return deriveShortDescription(richValue);
    }
    return valueOrEmpty(shortValue, false, "-");
  };
  const richDescriptionGetter = (params) => {
    const richValue = params?.data?.richDescription;
    if (typeof richValue === "string" && richValue.trim().length > 0) {
      return deriveShortDescription(richValue, MAX_RICH_DESCRIPTION_LENGTH);
    }
    const shortValue = params?.data?.description;
    return valueOrEmpty(shortValue, false, "-");
  };

  const bookingDataRef = useRef(null);
  const [bookingsLoaded, setBookingsLoaded] = useState(0);

  const fetchBookings = useCallback(async () => {
    const dateKey = JSON.stringify(selectedDateRange);
    if (lastDateKeyRef.current === dateKey) {
      return bookingDataRef.current;
    }
    lastDateKeyRef.current = dateKey;

    dispatchAction({ type: "fetching", payload: true });

    let dateFrom, dateTo;
    if (selectedDateRange[0] instanceof Date) {
      const dateFromString = moment(selectedDateRange[0]).format();
      const dateToString = moment(selectedDateRange[1]).format();
      const offset = moment.tz(user.timezone).format("Z");
      dateFrom = moment(replaceOffsetWithOther(dateFromString, offset)).utc().format();
      dateTo = moment(replaceOffsetWithOther(dateToString, offset, true))
        .utc()
        .format();
    } else {
      dateFrom = moment(selectedDateRange[0]).utc().format();
      dateTo = moment(selectedDateRange[1]).utc().format();
    }

    const bookingFilter = {
      and: [
        { active: { eq: true } },
        { and: [{ dateFrom: { le: dateTo } }, { dateTo: { ge: dateFrom } }] },
        { or: [{ reservedForUsers: { contains: user.user } }, { assistent: { contains: user.user } }, { createdBy: { eq: user.user } }] },
      ],
    };

    const dateFromDate = dateFrom.split("T")[0];
    const dateToDate = dateTo.split("T")[0];

    const [bookingsResult, entriesResult] = await Promise.all([
      getAllData({
        client,
        query: GET_BOOKING_ROWS_BY_SITE_NONE,
        dataPath: ["data", "getBookingRowsBySiteNone"],
        drillData: true,
        variables: {
          filter: JSON.stringify(bookingFilter),
          site: user.site,
          date: { between: [dateFromDate, dateToDate] },
          limit: 1000,
          nextToken,
        },
        cache: true,
        cacheTTLSeconds: 60,
      }),
      getAllData({
        client,
        query: BOOKING_BY_SITE_AND_DATE,
        dataPath: ["data", "bookingBySiteAndDate"],
        drillData: true,
        variables: {
          dateRange: [dateFrom, dateTo],
          site: user.site,
          filter: {
            active: { eq: true },
            or: [
              { reservedForUsers: { contains: user.user } },
              { assistent: { contains: user.user } },
              { createdBy: { eq: user.user } },
            ],
          },
          limit: 1000,
        },
        cache: true,
        cacheTTLSeconds: 60,
      }),
    ]);

    const bookings = (bookingsResult.items || []).filter(b => {
      const users = b.reservedForUsers || [];
      const assistants = Array.isArray(b.assistent) ? b.assistent : b.assistent ? [b.assistent] : [];
      return users.includes(user.user) || assistants.includes(user.user) || b.createdBy === user.user;
    });

    const individualEntriesMap = {};
    (entriesResult.items || []).forEach(booking => {
      (booking.bookingEntries?.items || []).forEach(entry => {
        const bookingId = entry.bookingId;
        const inventoryId = entry.equipment?.inventoryId || entry.inventoryId;
        if (bookingId && inventoryId) {
          const key = `${bookingId}_${inventoryId}`;
          if (!individualEntriesMap[key]) {
            individualEntriesMap[key] = [];
          }
          individualEntriesMap[key].push(entry);
        }
      });
    });

    bookings.forEach(b => {
      const seen = new Set();
      const bookedEquipments = [];
      Object.keys(individualEntriesMap).forEach(key => {
        if (key.startsWith(b.id + "_")) {
          const inventoryId = key.split("_").slice(1).join("_");
          if (inventoryId && !seen.has(inventoryId)) {
            seen.add(inventoryId);
            bookedEquipments.push(inventoryId);
          }
        }
      });
      b.bookedEquipments = bookedEquipments;
    });

    bookingDataRef.current = { bookings, individualEntriesMap };
    setBookingsLoaded(prev => prev + 1);
    return bookingDataRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, selectedDateRange, user.site, user.timezone]);

  const enrichWithInstruments = useCallback(async ({ bookings, individualEntriesMap }) => {
    const filterDef = filterState?.currentFilter?.filterDefinition || {};

    const equipmentMap = {};
    bookings.forEach((b) => {
      (b.bookedEquipments || []).forEach((id) => {
        if (!equipmentMap[id]) {
          equipmentMap[id] = {
            inventoryId: id,
            bookings: [],
          };
        }
        equipmentMap[id].bookings.push(b);
      });
    });

    const ids = Object.keys(equipmentMap);
    if (ids.length) {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      let loadInstrumentsResponse = await instrumentCache.loadInstruments(user.site, filterDef, {
        signal: abortRef.current.signal,
      });

      if (!loadInstrumentsResponse) {
        loadInstrumentsResponse = await instrumentCache.loadInstruments(user.site, filterDef);
      }

      if (!loadInstrumentsResponse) {
        loadInstrumentsResponse = { items: [] };
      }
      const { items: instruments } = loadInstrumentsResponse;
      instruments.forEach((inst) => {
        const instrumentId = `${inst.inventoryId}`;
        if (equipmentMap[instrumentId]) {
          Object.assign(equipmentMap[instrumentId], {
            instrumentId,
            ...inst,
          });
        }
      });
    }

    for (const key of Object.keys(equipmentMap)) {
      if (!equipmentMap[key]?.instrumentId) {
        delete equipmentMap[key];
      }
    }

    const equipmentRows = [];
    Object.values(equipmentMap).forEach(eq => {
      eq.bookings.forEach(b => {
        const status = (b.approval?.status ?? (b.active ? "ACTIVE" : "CANCELLED")) ?? eq.status;
        const entriesKey = `${b.id}_${eq.inventoryId}`;
        const individualEntries = individualEntriesMap[entriesKey];

        const buildRow = (entry, idx) => ({
          uniqId: `${eq.inventoryId}_${b.id}_${idx}`,
          id: b.id,
          inventoryId: eq.inventoryId,
          type: b.type,
          approval: b.approval,
          equipmentId: eq.equipmentId,
          equipmentNickName: eq.equipmentNickName,
          equipmentModel: eq.equipmentModel,
          serialNumber: eq.serialNumber,
          location: eq.location,
          locationKey: eq.locationKey,
          displayImage: eq.displayImage,
          shortIdentifier: eq.shortIdentifier,
          manufacturer: eq.manufacturer,
          isHazardous: eq.isHazardous,
          assistedBooking: eq.assistedBooking,
          status,
          active: status === "ACTIVE" || status === "PENDING",
          bookedUsers: b.bookedUsers,
          reservedForEmails: b.reservedForEmails,
          reservedForUsers: b.reservedForUsers,
          project: b.project,
          redPandaProjects: b.redPandaProjects,
          description: b.description,
          richDescription: b.richDescription,
          checkIn: b.checkInRequired,
          dateFrom: entry?.start || b.dateFrom,
          dateTo: entry?.end || b.dateTo,
          bookedEquipments: b.bookedEquipments,
        });

        if (individualEntries?.length > 0) {
          individualEntries.forEach((entry, idx) => equipmentRows.push(buildRow(entry, idx)));
        } else {
          const fallbackEntry = (b.bookingEntries || []).find(entry => entry.inventoryId === eq.inventoryId);
          equipmentRows.push(buildRow(fallbackEntry, 0));
        }
      });
    });
    hadDataRef.current = true;
    dispatchAction({
      type: "nextToken",
      payload: { token: null, data: equipmentRows },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentCache, user.site, filterState]);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBookings]);

  useEffect(() => {
    if (!isFilterDataReady || !bookingDataRef.current) {
      return;
    }
    if (debouncedFetchRef.current) {
      clearTimeout(debouncedFetchRef.current);
    }
    if (!hadDataRef.current) {
      enrichWithInstruments(bookingDataRef.current);
    } else {
      debouncedFetchRef.current = setTimeout(() => {
        enrichWithInstruments(bookingDataRef.current);
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterDataReady, enrichWithInstruments, bookingsLoaded]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (debouncedFetchRef.current) {
        clearTimeout(debouncedFetchRef.current);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    if (shouldFetchData) {
      dispatchAction({ type: "fetching", payload: true });
      lastDateKeyRef.current = null;
      bookingDataRef.current = null;
      hadDataRef.current = false;
      clearGetAllDataCache();
      fetchBookings();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchData]);
  const {
    currentFilterChip,
    onApplyColumns,
    currentColumnsChip,
    dateFromFormatter,
    dateToFormatter,
    onSave,
    icons,
    onRowSelected,
    onChipDelete,
    editBooking,
  } = useBooking({
    filterState,
    displayPreset,
    defaultShowColumns,
    gridRef,
    onAgGridColumnApply,
    savedFilter,
    isFilterDataReady,
    updateCurrentFilter,
    loadFilterData,
    user,
    gridApi,
    searchQuery,
    client,
    localUserFilters: safeLocalUserFilters,
    setUserFilters,
    setLastFavFilter,
    setUserDisplayColumnMaster,
    setLastDisplayColumns,
    userFilters,
    userDisplayColumnMaster,
    setSelectedEquipment,
    selectedEquipment,
    setDisplayPreset,
    setSavedFilter,
    loadPendingReservations,
    dispatchAction,
    bookingDispatchAction,
  });

  const actionRender = (params) => {
    return (
      <BookingMenuAction
        params={params}
        handlers={{ deleteEquipment, editBooking, setRowEquipmentId, setPopupEnableStatus, changeEndTimeOpen }}
      />
    );
  };

  const overrideCellRender = (key, defaultObj) => {
    switch (key) {
      case "type":
        defaultObj = {
          ...defaultObj,
          filter: false,
          cellRenderer: (item) =>
            item?.data?.type ? <BookingTypeChip key={item.data.id} item={item.data} /> : EN_DASH,
          filterValueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
          valueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
        };
        break;
      case "checkIn":
        defaultObj = {
          ...defaultObj,
          cellRenderer: (item) => (hasCheckInState(item?.data) ? <CheckInButton key={item.data.id} item={item.data} /> : null),
        };
        break;
      case "dateFrom":
        defaultObj = {
          ...defaultObj,
          checkboxSelection: true,
          showDisabledCheckboxes: true,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: dateFromFormatter,
          filterValueGetter: dateFromFormatter,
          cellRenderer: DateWithRecurrenceCellRenderer,
        };
        break;
      case "dateTo":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: dateToFormatter,
          filterValueGetter: dateToFormatter,
        };
        break;
      case "active":
        defaultObj = {
          ...defaultObj,
          valueGetter: statusGetter,
          filterValueGetter: statusGetter,
        };
        break;

      case "hazardous":
        defaultObj = {
          ...defaultObj,
          filter: true,
          cellRenderer: (item) => <BookingHazardousChip key={item.data.id} item={item.data} />,
          filterValueGetter: hazardousStatusGetter,
          valueGetter: hazardousStatusGetter,
        };
        break;
      case "location":
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => params?.data?.locationKey || params?.data?.locationKey,
          filterValueGetter: (params) => params?.data?.locationKey || params?.data?.locationKey,
        };
        break;
      case "bookingFor":
      case "reservedForUsers":
        defaultObj = {
          ...defaultObj,
          valueGetter: reservedForGetter,
          filterValueGetter: reservedForFilterFormatterGetter,
        };
        break;
      case "shouldAssisted":
        defaultObj = {
          ...defaultObj,
          cellClass: "action-render",
          filter: "agSetColumnFilter",
          filterParams: {
            defaultToNothingSelected: true,
            showTooltips: true,
            buttons: ["reset"],
            values: (params) => params.success(["Yes", "No"]),
          },
          filterValueGetter: (params) => (params?.data?.shouldAssisted === "1" ? "Yes" : "No"),
        };
        break;
      case "project":
        defaultObj = {
          ...defaultObj,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          cellRenderer: ProjectRenderer,
          filterValueGetter: projectGetter,
          cellStyle: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        };
        break;
      case "description":
        defaultObj = {
          ...defaultObj,
          hide: true,
          suppressColumnsToolPanel: true,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          valueGetter: descriptionGetter,
          filterValueGetter: descriptionGetter,
          cellRenderer: RichDescriptionCellRenderer,
        };
        break;
      case "richDescription":
        defaultObj = {
          ...defaultObj,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          valueGetter: richDescriptionGetter,
          filterValueGetter: richDescriptionGetter,
          cellRenderer: RichDescriptionCellRenderer,
        };
        break;
      case "actionButton":
        defaultObj = {
          ...defaultObj,
          width: 0,
          maxWidth: 50,
          field: "actions",
          headerName: "",
          filter: false,
          pinned: "right",
          lockVisible: true,
          suppressColumnsToolPanel: true,
          suppressMenu: true,
          lockPosition: "right",
          cellClass: "action-render",
          cellRenderer: actionRender,
        };

        break;

      case "detailExpander":
        defaultObj = {
          ...defaultObj,
          cellRenderer: "agGroupCellRenderer",
          pinned: "left",
        };

        break;

      case "createdBy": {
        const createdByGetter = (params) => {
          const creatorId = params?.data?.createdBy;
          if (!creatorId) return "-";
          const bookedUsers = params?.data?.bookedUsers ?? [];
          const creator = bookedUsers.find((u) => (u.user || u.userId) === creatorId);
          if (creator) {
            return creator.name || `${creator.givenName ?? ""} ${creator.familyName ?? ""}`.trim() || creator.email;
          }
          return params?.data?.createdByEmail || creatorId;
        };
        defaultObj = {
          ...defaultObj,
          valueGetter: createdByGetter,
          filterValueGetter: createdByGetter,
        };
        break;
      }
      case "createdAt":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          valueFormatter: (params) =>
            params?.data?.createdAt ? moment(params.data.createdAt).tz(user.timezone).format("DD-MMM-YYYY, hh:mm A") : "-",
          filterValueGetter: (params) =>
            params?.data?.createdAt ? moment(params.data.createdAt).tz(user.timezone).format("DD-MMM-YYYY, hh:mm A") : "-",
        };
        break;

      default:
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => valueOrEmpty(params?.data[key] ?? "-", false, "-"),
          filterValueGetter: (params) => valueOrEmpty(params?.data[key] ?? "-", false),
        };
        break;
    }
    return defaultObj;
  };

  const tableColumn = useMemo(() => {
    const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
    const isPredOrGredUser = !!(user?.isPredUser || user?.isGredUser);
    // Equipment columns are keyed into the same map, so a later entry replaces an earlier one. That
    // is wanted for the shared keys (equipmentId, serialNumber, ...), where the schema supplies the
    // filter and renderer. It is not wanted for booking's own locked columns: `description` exists
    // on both sides and means different things - the booking's description here, the equipment's in
    // the schema. Booking keeps the keys it locks.
    const bookingOwned = new Set(
      Object.keys(BookingMetaByEquipment.fields).filter((key) => BookingMetaByEquipment.fields[key].lockVisible),
    );
    const dataModelFull = [
      ...Object.entries(BookingMetaByEquipment.fields)
        .filter(([, meta]) => !meta.adminOnly || isBookingAdmin)
        .filter(([, meta]) => !meta.siteRestricted || isPredOrGredUser)
        .map(([key, meta]) => ({ key, meta })),
      ...dataModel.filter((col) => !bookingOwned.has(col.key)).map((col) => ({ key: col.key, meta: col })),
    ];

    const baseMap = dataModelFull.reduce((acc, { key, meta }) => {
      acc[key] = overrideCellRender(key, {
        headerName: meta.val ?? meta.text,
        field: key,
        sortable: meta.sortable ?? true,
        floatingFilter: true,
        filter: meta.filter ?? true,
        filterParams: meta.filterParams,
        resizable: true,
        headerTooltip: meta.val ?? meta.text,
        width: meta.width || 200,
        maxWidth: meta.maxWidth || 200,
        minWidth: meta.minWidth || 200,
        ...meta,
      });

      return acc;
    }, {});

    return Object.values(baseMap); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BookingMetaByEquipment.fields, dataModel, user, usersList]);

  const onGridReady = (params) => {
    setGridApi(params);
  };

  useEffect(() => {
    if (!gridApi?.api || !innerTableFilters) return;
    if (Object.keys(innerTableFilters).length === 0) return;
    const sanitizedFilters = sanitizeFilterModelForGrid(gridApi.api, innerTableFilters);
    gridApi.api.setFilterModel(sanitizedFilters);
  }, [gridApi, innerTableFilters]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      suppressMenu: true,
      sortable: true,
    };
  }, []);

  useEffect(() => {
    if (gridApi && resetAll) {
      gridRef.current.api.setFilterModel();
      setInnerTableFilters && setInnerTableFilters({});
      setSearchQuery("");
      setSelectedEquipment([]);
      setSelectedDateRange([
        get4WeekStartAndEndInISOString("start", user.timezone),
        get4WeekStartAndEndInISOString("end", user.timezone),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAll]);

  useEffect(() => {
    safeLocalUserFilters.current = userFilters; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilters]);

  const isRowSelectable = useMemo(() => {
    return (params) => {
      const node = params.level;
      const isGroupRow = node === 0;
      const isActive =
        params.data.active === true &&
        moment(new Date(params?.data?.dateFrom)).tz(user.timezone).format("YYYY-MM-DD HH:mm") >
          moment(new Date()).tz(user.timezone).format("YYYY-MM-DD HH:mm");
      if (isGroupRow) {
        params.selectable = isActive;
      }
      return isGroupRow && isActive;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    setTempRowData(data); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const scrollEvent = () => {
    const container = document.querySelector(".grid-container");
    if (Math.ceil(container.clientHeight + container.scrollTop) >= container.scrollHeight && nextToken) {
      dispatchAction({ type: "nextData" });
    }
  };

  const deleteEquipment = ({ type = "cancel" } = {}) => {
    setIsDialogOpen(true);
    setDropType(type);
    setIsDropBooking(true);
  };

  const sideBarDef = useMemo(() => {
    const getOpenedToolPanelSafely = () => {
      try {
        return gridRef?.current?.api?.getOpenedToolPanel?.() || null;
      } catch (_error) {
        return null;
      }
    };

    const columnsDef = {
      minWidth: 200,
      width: 276,
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "column-icon",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnSelectAll: true,
      },
    };

    const filtersDef = {
      minWidth: 200,
      width: 276,
      id: "filters",
      labelDefault: "Filters",
      labelKey: "filters",
      iconKey: "filter",
      toolPanel: "agFiltersToolPanel",
    };

    const saveDef = {
      minWidth: 200,
      width: 276,
      id: "save_configuration",
      labelDefault: "Saved configurations",
      labelKey: "loading",
      iconKey: "setting-icon",
      toolPanel: () => {
        const savedChips =
          currentSideBarPanel === TOOL_PANELS.filters
            ? savedFilterChips.map((f) => ({ ...f, chipName: f.chipName || f.filterName }))
            : savedColumnChips.map((c) => ({ ...c, chipName: c.chipName || c.displayName }));

        const savedChip =
          currentSideBarPanel === TOOL_PANELS.filters ? { ...currentFilterChip } : { ...currentColumnsChip };

        const sideBars = gridRef?.current?.api?.getSideBar();
        const panels = sideBars
          ? sideBars.toolPanels?.map((p) => p.id)
          : [TOOL_PANELS.filters, TOOL_PANELS.saveConfiguration];

        return (
          <SaveConfiguration
            storeUser={user}
            savedChips={savedChips}
            savedChip={savedChip}
            currentSideBarPanel={currentSideBarPanel}
            gridRef={gridRef}
            onApplyColumns={onApplyColumnsFromChip}
            onApplyFilter={onApplyFilterFromChip}
            defaultShowColumns={defaultShowColumns}
            defaultFilterObj={DEFAULT_FILTER}
            sideBarPanels={[...panels]}
            user={user}
            onSave={onSave}
            onChipDelete={onChipDelete}
          />
        );
      },
    };
    const toolPanels = currentSideBarPanel === TOOL_PANELS.columns ? [columnsDef, saveDef] : [filtersDef, saveDef];

    return {
      toolPanels,
      defaultToolPanel: gridRef?.current?.api?.isSideBarVisible()
        ? getOpenedToolPanelSafely() || currentSideBarPanel
        : currentSideBarPanel,
      hiddenByDefault: !showSideBar,
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showSideBar,
    currentSideBarPanel,
    gridRef,
    savedFilterChips,
    savedColumnChips,
    currentFilterChip,
    currentColumnsChip,
    displayPreset,
    user,
    filterState?.currentFilter,
    onApplyColumnsFromChip,
    onApplyFilterFromChip,
  ]);

  const attr = useMemo(
    () => ({
      height: "100%",
      columnDefs: tableColumn,
      animateRows: true,
      rowExport: false,
      includeHiddenColumnsInQuickFilter: true,
      sideBar: sideBarDef,
      allowContextMenuWithControlKey: true,
      getContextMenuItems: (params) => {
        return [
          "copy",
          "copyWithHeaders",
          "copyWithGroupHeaders",
          "separator",
          {
            name: "Export",
            subMenu: [
              {
                name: "Export Selected Rows",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
              "separator",
              {
                name: "Export All (Filtered)",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                        onlySelected: false,
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                        onlySelected: false,
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
            ],
          },
        ];
      },
      defaultCsvExportParams: createDefaultCsvExportParams({}, {}, user),
      defaultExcelExportParams: createDefaultExcelExportParams({}, {}, user),
      processCellForClipboard: createClipboardCellProcessor({}, user),
      defaultToolPanel: "filters",
      hiddenByDefault: true,
      rowData: tempRowData,
      icons: icons,
      onGridReady,
    }),
    [tableColumn, sideBarDef, tempRowData, icons, user],
  );

  return (
    <>
      <BackdropPortal show={fetching} />

      <div
        className="grid-container"
        onScroll={scrollEvent}
        style={{
          width: "100%",
          height: "100%",
          minHeight: "415px",
        }}
      >
        <DLabGrid
          {...attr}
          pagination={false}
          suppressPaginationPanel={false}
          defaultColDef={defaultColDef}
          rowSelection={"multiple"}
          suppressRowClickSelection={true}
          autoGroupColumnDef={"autoGroupColumnDef"}
          onRowSelected={onRowSelected}
          isRowSelectable={isRowSelectable}
          gridRef={gridRef}
          rowStyle={rowStyle}
          onFilterChanged={(params) => {
            const filters = params.api.getFilterModel();
            const gridValidatedFilters = sanitizeFilterModelForGrid(params.api, filters);
            const sanitizedFilters = sanitizeGridFilterModel(gridValidatedFilters);
            setInnerTableFilters && setInnerTableFilters(sanitizedFilters);
            if (Object.keys(sanitizedFilters).length > 0) {
              setDisableResetAll(false);
            }
          }}
        />
      </div>
      <BookingsPaginationActions
        selectedCount={selectedEquipment?.length}
        onCancelClick={() => setOpenMultiDeleteDialog(true)}
      />
      <ChangeBookingEndTimeModal
        isOpen={isChangeEndTimeOpen}
        onClose={handleCloseChangeEndTimeModal}
        booking={bookingForEndTimeChange}
        onSave={(booking, newEndTime) => {
          handleSaveEndTime(booking, newEndTime);
          clearGetAllDataCache();
        }}
        initialSelectedResourceId={bookingForEndTimeChange?.instrumentId}
      />
    </>
  );
};

export default compose(
  connect(null, {
    loadPendingReservations: loadPendingReservationsAction,
  }),
  withApollo,
)(EquipmentBookingList);
